import clsx from 'clsx'
import pluralize from 'pluralize'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { FvButton, Icon, OrDivider } from '@fv/client-components'
import { submitForm } from '@fv/client-core'
import type {
  ClientPackagingType,
  EquipmentAccessorial,
  LoadEquipment,
} from '@fv/client-types'
import { type EquipmentType, isFlatbedEquipment } from '@fv/models/equipment'

import { ValidatedForm } from '@/components/inputs'
import { List, ListItem } from '@/components/List'
import { PillHeader } from '@/components/PillHeader'
import { Pinline } from '@/components/Pinline'
import { ShipperSlider } from '@/components/ShipperSlider'
import EquipmentAccessorialConfig from '@/components/shared/EquipmentAccessorialConfig'
import { Price } from '@/components/shared/Price'
import type { FormCommodity } from '@/features/commodities/FormCommodity'
import {
  usePrettyNumber,
  useQuoteSettings,
  useTypeNames,
} from '@/hooks/settings'
import type { AccessorialTypeEnum } from '@/hooks/settings/useAccessorialOptions'

import { buildFormCommodity } from '../commodities/commodityUtils'
import { composeEquipmentAccessorialsDescription } from '../shipment-equipment-form/utils'
import { useBookingForm } from './hooks'
import { LoadItemFields } from './LoadItemFields'
import { TLBookingSection } from './TLBookingSection'
import { type BookFormLoad, composeLoadKey } from './types'

const hidden = { display: 'none' }
const noOp = (): null => null
const tempControlFields = ['min', 'max', 'uom']

type Props = {
  load: BookFormLoad
}

export const BookingLoad = ({ load }: Props) => {
  const prettyNumber = usePrettyNumber()
  const { equipment, loadId, type, weight, weightUOM } = load
  const { setFormRef, ...form } = useBookingForm()
  const { equipmentName, packageName, accessorialName } = useTypeNames()

  const quote = form.selectedQuotes.find(q => q._id === load.quoteId)
  const accessorialOptions = useAccessorialOptions(
    equipment,
    quote?.equipmentType,
  )

  const isMultiStop = form.locations.length > 2
  const canAddItems = !form.isPickupOnly && !isMultiStop
  const isTempControlRequired = !!accessorialOptions.find(o => o.key === 'temp')
  const requiresDeclaredValue = form.settings.requiresDeclaredValue
  const requiresFlatbedDetails =
    form.settings.requiresFlatbedDetails && isFlatbedEquipment(equipment.type)
  const tempSettings = equipment.accessorials?.find(a => a.key === 'temp')

  const formRef = useRef<HTMLFormElement>(null)
  const accessorialFormRef = useRef<HTMLFormElement>(null)
  const sectionKey = composeLoadKey(loadId)
  const isEditing = form.activeSection === sectionKey
  const section = form.formSections.find(s => s.key === sectionKey)
  const hasSection = !!section

  const [showAccessorialPanel, setShowAccessorialPanel] = useState(false)
  const onClose = () => {
    const isValid = accessorialFormRef.current?.checkValidity()
    if (isValid) return setShowAccessorialPanel(false)
    submitForm(accessorialFormRef.current)
  }

  useEffect(() => {
    if (!hasSection) return
    setFormRef(sectionKey, formRef)
  }, [hasSection, sectionKey, setFormRef])

  if (!hasSection) return null

  function editLoad() {
    form.activateSection(sectionKey)
  }

  function setAccessorials(data: { accessorials: EquipmentAccessorial[] }) {
    const tempControl = data.accessorials.find(a => a.key === 'temp')

    if (isTempControlRequired && !tempControl) {
      toast.error(
        'Temp control is required for refrigerated equipment types.',
        { id: 'tempControl' },
      )
      return
    }

    form.setLoads(prev =>
      prev.map(load => {
        if (load.loadId !== loadId) return load
        return {
          ...load,
          equipment: { ...load.equipment, ...data },
        }
      }),
    )
  }

  function onInvalidSubmit(invalidEl?: HTMLInputElement) {
    if (
      isTempControlRequired &&
      tempControlFields.includes(invalidEl?.name ?? '')
    ) {
      const tempAccessorial: EquipmentAccessorial = {
        key: 'temp',
        ...tempSettings,
      }
      // Ensure temp control accessorial is selected
      const nextAccessorials: EquipmentAccessorial[] = [
        ...(equipment.accessorials ?? []).filter(a => a.key !== 'temp'),
        tempAccessorial,
      ]
      setAccessorials({ accessorials: nextAccessorials })
      setShowAccessorialPanel(true)
    }
  }

  function addCommodity() {
    form.setLoads(prev =>
      prev.map(load => {
        if (load.loadId !== loadId) return load

        return {
          ...load,
          items: load.items.concat(buildFormCommodity({})),
        }
      }),
    )
  }

  function setValues(id: string) {
    return (item: FormCommodity | null) => {
      form.setLoads(prev =>
        prev.map(load => {
          if (load.loadId !== loadId) return load

          return {
            ...load,
            items: item
              ? load.items.map(i => (i.id === id ? item : i))
              : load.items.filter(c => c.id !== id),
          }
        }),
      )
    }
  }

  return (
    <>
      <TLBookingSection isEditing={isEditing} isValid={section?.isValid}>
        <PillHeader
          st
          fixedWidthIcon
          className={clsx(
            'beer-container -top-3.5 z-10 mb-3 flex min-h-[3.5rem] items-center !px-2',
            !isEditing &&
              !section?.isValid &&
              '-ml-[calc(.5rem)] w-[calc(100%-3rem)]',
            isEditing && 'absolute left-[calc(1.5rem)] w-[calc(100%-7rem)]',
            section?.isValid &&
              'absolute -left-[calc(.5rem+1px)] w-[calc(100%-3rem+1px)]',
          )}
        >
          {section?.isValid && (
            <FvButton theme="round" onClick={() => editLoad()} icon="pen-alt" />
          )}
          <div
            className={clsx(
              'flex items-center',
              (isEditing || !section?.isValid) && 'ml-2',
            )}
          >
            <Icon
              icon={quote || load.isFreightCollect ? 'truck' : 'ban'}
              className="text-fv-gray-dark"
              transform="up-1"
            />
            <p className="mb-0">
              {equipmentName(quote?.equipmentType ?? type)}
              {quote && ` via ${quote.providerName}`} / {prettyNumber(weight)}{' '}
              {weightUOM}.
              {!quote && !load.isFreightCollect && ' / No carrier selected'}
            </p>
          </div>
          <div className="ml-auto mr-4">
            {!quote && !load.isFreightCollect && 'NA'}
            {quote && <Price amount={quote.amount} currency={quote.currency} />}
          </div>
        </PillHeader>

        {isEditing && (
          <ValidatedForm
            onInvalidSubmit={onInvalidSubmit}
            onValidSubmit={form.goToNextSection}
            ref={formRef}
          >
            <div
              className={clsx('mt-2', {
                'mb-4': !canAddItems,
                'mb-6': canAddItems,
              })}
            >
              <a
                href="#"
                onClick={e => {
                  e.preventDefault()
                  setShowAccessorialPanel(true)
                }}
              >
                <Icon icon="list" />
                <span>Equipment accessorials</span>
              </a>
              :{' '}
              <span>
                {composeEquipmentAccessorialsDescription(
                  accessorialName,
                  equipment.accessorials,
                )}
              </span>
              {isTempControlRequired &&
                tempControlFields.map(f => {
                  const key = f as keyof EquipmentAccessorial
                  const value = tempSettings?.[key] ?? ''

                  // Hidden input for accessorial validation only
                  return (
                    <input
                      key={f}
                      name={f}
                      onChange={noOp}
                      required
                      style={hidden}
                      value={value}
                    />
                  )
                })}
            </div>

            {canAddItems ? (
              load.items.map((values, i) => (
                <LoadItemFields
                  autoFocus={!values.quantity && i === 0}
                  canRemove={load.items.length > 1}
                  className=""
                  key={values.id}
                  locations={form.locations}
                  requiresDeclaredValue={requiresDeclaredValue}
                  requiresDims={requiresFlatbedDetails}
                  setValues={setValues(values.id)}
                  values={values}
                />
              ))
            ) : (
              <Pinline rule className="mb-8" />
            )}
            <div className="mt-8 flex items-center gap-x-3">
              <FvButton
                icon="arrow-down-to-line"
                theme="secondary"
                fwd
                type="submit"
              >
                Looks good, proceed
              </FvButton>
              {canAddItems && <OrDivider className="ml-2" />}
              {canAddItems && (
                <FvButton icon="plus" onClick={addCommodity}>
                  <span>Add another handling unit group</span>
                </FvButton>
              )}
            </div>
          </ValidatedForm>
        )}

        {section?.isValid && (
          <div className="booking-step__summary">
            <List fcp0 lcp0>
              <ListItem>
                <Icon
                  className="text-fv-gray-dark"
                  icon="forklift"
                  transform="down-0"
                />
                <span>
                  {form.isPickupOnly
                    ? 'Commodities to be added later.'
                    : composeLoadSummary({
                        commodities: load.items,
                        getPackageName: packageName,
                        prettyNumber,
                      })}
                </span>
              </ListItem>
            </List>
          </div>
        )}
      </TLBookingSection>

      <ShipperSlider isOpen={showAccessorialPanel} onClose={onClose}>
        <p className="mb-0">
          <Icon icon="truck" className="text-fv-gray-dark" />
          <span>
            {equipmentName(quote?.equipmentType ?? type)}
            {quote && ` via ${quote.providerName}`} / {prettyNumber(weight)}{' '}
            {weightUOM}.{!quote && ' / No carrier selected'}
          </span>
        </p>
        <Pinline rule className="mb-8" />
        <ValidatedForm ref={accessorialFormRef}>
          <div className="booking-accessorial-options">
            <EquipmentAccessorialConfig
              accessorialOptions={accessorialOptions}
              accessorials={equipment.accessorials}
              id={loadId}
              setAccessorials={setAccessorials}
            />
          </div>
        </ValidatedForm>
      </ShipperSlider>
    </>
  )
}

function composeLoadSummary({
  commodities,
  getPackageName,
  prettyNumber,
}: {
  commodities: FormCommodity[]
  getPackageName: (t: ClientPackagingType) => string
  prettyNumber: (n?: string | number | null | undefined) => string | undefined
}) {
  return `Hauling ${commodities
    .map(i => i.description)
    .join(', ')} / ${commodities
    .map(i => pluralize(getPackageName(i.type), Number(i.quantity), true))
    .join(', ')} / ${commodities
    .map(i => `${prettyNumber(i.weight)} ${i.weightUOM}.`)
    .join(', ')}`
}

function useAccessorialOptions(
  equipment: LoadEquipment,
  quotedEquipmentType?: EquipmentType,
): AccessorialTypeEnum[] {
  const settingsQuery = useQuoteSettings('truckload')
  const equipmentOpts = settingsQuery.data?.equipmentOptions ?? []
  const activeEquipment = equipmentOpts.find(
    o => o.key === (quotedEquipmentType ?? equipment.type),
  )

  const activeAccessorialOpts = activeEquipment?.accessorialOptions ?? []
  if (!quotedEquipmentType || quotedEquipmentType === equipment.type) {
    return activeAccessorialOpts
  }

  // Return current accessorials and options for alt equipment type
  const primaryEquipment = equipmentOpts.find(o => o.key === equipment.type)
  const primaryAccessorialOpts = primaryEquipment?.accessorialOptions ?? []
  const currentAccessorialOpts = primaryAccessorialOpts.filter(
    o => !!equipment.accessorials?.find(a => a.key === o.key),
  )

  return [...currentAccessorialOpts, ...activeAccessorialOpts]
}
